import { Text } from "@chakra-ui/react";
import { Popover } from "frost-ui-react";
import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { ColorType, getFontColor } from "../../../configs/constants/ColorType";
import AppText from "../../Core/AppText/AppText";

type Size = "s" | "m";
export interface TagProps {
	color: ColorType;
	text: string;
	textColor?: ColorType;
	className?: string;
	size?: Size;
	textOnHover?: string;
	width?: string;
}

const Tag: React.FC<TagProps> = ({
	text,
	textColor = "white",
	color,
	className,
	size = "m",
	textOnHover,
	width,
}) => {
	const tagRef = useRef<HTMLDivElement>(null);
	const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

	const showTooltip = () => {
		const rect = tagRef.current?.getBoundingClientRect();
		if (rect) setCoords({ top: rect.top + 35, left: rect.left });
	};

	return (
		<StyledTag
			ref={tagRef}
			backgroundColor={color}
			className={className}
			size={size}
			onMouseEnter={showTooltip}
			onMouseLeave={() => setCoords(null)}
			width={width}
		>
			<Text
				fontWeight="medium"
				textColor={textColor}
				fontSize={size === "m" ? "xms" : "xs"}
				lineHeight={1}
			>
				{text}
			</Text>
			{textOnHover &&
				coords &&
				/* Portal to body so the tooltip escapes ancestor `overflow` clipping
				   (e.g. the scrollable, narrow job-header panel). Positioned fixed to
				   the tag's on-screen rect. Cast works around the duplicate
				   @types/react versions pulled in via react-dom. */
				(createPortal(
					<div
						className="tag-hover-text"
						style={{
							position: "fixed",
							top: coords.top,
							left: coords.left,
							zIndex: 1000,
							// Multi-line hover texts join lines with \n — render the breaks.
							whiteSpace: "pre-line",
						}}
					>
						<Popover
							styles={{ width: 200, arrowSize: 10, offset: "10px", padding: 8 }}
							pointTo="top"
						>
							<AppText color="grayText" size="s">
								{textOnHover}
							</AppText>
						</Popover>
					</div>,
					document.body
				) as unknown as React.ReactNode)}
		</StyledTag>
	);
};

const StyledTag = styled.div<{ backgroundColor: ColorType; size: Size; width?: string }>`
	position: relative;
	user-select: none;
	padding: ${(props) => (props.size === "m" ? "4px 10px" : "2px 3px")};
	border-radius: 2px;
	background-color: ${(props) => getFontColor(props.backgroundColor, props.theme)};
	display: inline-block;
	${(props) => props.width && `width:${props.width};`}
`;

export default Tag;
